import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'

const getIssueSubscriptions = async (token, userId) => {
  const url = `users/${userId}/issue_subscriptions`
  const data = await apiGet({ url, token })
  return data
}

export const useIssueSubscriptions = ({ options } = {}) => {
  const token = useContext(AuthContext)
  const { id: userId } = useContext(UserContext)
  const queryFn = () => getIssueSubscriptions(token, userId)
  const queryKey = ['users', userId, 'issue_subscriptions']

  return useQuery(queryKey, queryFn, {
    ...options
  })
}

const createIssueSubscription = async (token, userId, params) => {
  const url = `users/${userId}/issue_subscriptions`
  const data = await apiPost({ url, token, objectName: 'issue_subscription', params })
  return data
}

export const useCreateIssueSubscription = ({ options } = {}) => {
  const token = useContext(AuthContext)
  const { id: userId } = useContext(UserContext)
  const queryClient = useQueryClient()

  return useMutation(
    (params) => createIssueSubscription(token, userId, params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users', userId, 'issue_subscriptions'])
      },
      ...options
    }
  )
}

const updateIssueSubscription = async (token, userId, subscriptionId, params) => {
  const url = `users/${userId}/issue_subscriptions/${subscriptionId}`
  const data = await apiPatch({ url, token, objectName: 'issue_subscription', params })
  return data
}

export const useUpdateIssueSubscription = ({ options } = {}) => {
  const token = useContext(AuthContext)
  const { id: userId } = useContext(UserContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ subscriptionId, params }) => updateIssueSubscription(token, userId, subscriptionId, params),
    {
      onSuccess: (_data, { subscriptionId }) => {
        queryClient.invalidateQueries(['users', userId, 'issue_subscriptions'])
        queryClient.invalidateQueries(['users', userId, 'issue_subscriptions', subscriptionId])
      },
      ...options
    }
  )
}

const deleteIssueSubscription = async (token, userId, subscriptionId) => {
  const url = `users/${userId}/issue_subscriptions/${subscriptionId}`
  const data = await apiDelete({ url, token })
  return data
}

export const useDeleteIssueSubscription = ({ options } = {}) => {
  const token = useContext(AuthContext)
  const { id: userId } = useContext(UserContext)
  const queryClient = useQueryClient()

  return useMutation(
    (subscriptionId) => deleteIssueSubscription(token, userId, subscriptionId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users', userId, 'issue_subscriptions'])
      },
      ...options
    }
  )
}
